"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import cnBind from "classnames/bind";
import { useTranslation } from "react-i18next";

import { chatWidgetMessageApi } from "@/api/chatWidgetMessageApi";
import { chatWidgetRepliesApi } from "@/api/chatWidgetRepliesApi";
import { useAuth } from "@/shared/auth/AuthContext";
import { useChatWidgetRepliesSocket } from "@/shared/hooks/useChatWidgetSocket";
import styles from "./AgentChatWidget.module.scss";

const cx = cnBind.bind(styles);

type ChatMessage = {
    id: string;
    role: "agent" | "user";
    text: string;
};

type VisitorMeta = {
    pageUrl: string;
    referrer: string;
    userAgent: string;
    language: string;
    timezone: string;
};

/** Событие для программного открытия виджета (например, кнопка «Связь с менеджером» в кабинете). */
export const OPEN_AGENT_CHAT_WIDGET_EVENT = "tezam-open-agent-chat-widget";

const STORAGE_GUEST_SESSION = "tezam-chat-session";
const STORAGE_CHAT_OPEN = "tezam-chat-open";
const STORAGE_LAST_READ = "tezam-chat-last-read";

const messagesStorageKey = (sessionId: string) => `tezam-chat-messages:${sessionId}`;
const contactStorageKey = (sessionId: string) => `tezam-chat-contact:${sessionId}`;
const visitorNameStorageKey = (sessionId: string) => `tezam-chat-visitor-name:${sessionId}`;
const lastReadStorageKey = (sessionId: string) => `${STORAGE_LAST_READ}:${sessionId}`;

const LEAD_FORM_AFTER_USER_MESSAGES = 5;

const readPersistedOpen = (): boolean => {
    if (typeof window === "undefined") return false;
    try {
        return window.sessionStorage.getItem(STORAGE_CHAT_OPEN) === "1";
    } catch {
        return false;
    }
};

const writePersistedOpen = (open: boolean) => {
    if (typeof window === "undefined") return;
    try {
        window.sessionStorage.setItem(STORAGE_CHAT_OPEN, open ? "1" : "0");
    } catch {
        /* ignore quota / private mode */
    }
};

const readLastReadId = (sessionId: string): string | null => {
    if (typeof window === "undefined" || !sessionId) return null;
    try {
        return window.sessionStorage.getItem(lastReadStorageKey(sessionId));
    } catch {
        return null;
    }
};

const writeLastReadId = (sessionId: string, messageId: string) => {
    if (typeof window === "undefined" || !sessionId || !messageId) return;
    try {
        window.sessionStorage.setItem(lastReadStorageKey(sessionId), messageId);
    } catch {
        /* ignore */
    }
};

const countUnreadAgentMessages = (items: ChatMessage[], lastReadId: string | null): number => {
    if (!items.length) return 0;
    if (!lastReadId) {
        // First visit in this tab: treat existing history as already seen.
        return 0;
    }
    const idx = items.findIndex((m) => m.id === lastReadId);
    const after = idx >= 0 ? items.slice(idx + 1) : items;
    return after.filter((m) => m.role === "agent" && m.id !== "welcome" && m.id !== "ask-name").length;
};

/** Гость: одна анонимная сессия на браузер (localStorage). */
const getOrCreateGuestSessionId = (): string => {
    if (typeof window === "undefined") return "";
    const existing = window.localStorage.getItem(STORAGE_GUEST_SESSION);
    if (existing) return existing;

    const nextId =
        typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `chat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    window.localStorage.setItem(STORAGE_GUEST_SESSION, nextId);
    return nextId;
};

/** Авторизованный клиент (не сотрудник): отдельный диалог на аккаунт, без общей «гостевой» сессии. */
const getAuthenticatedClientSessionId = (userId: string) => `client-${userId}`;

const getVisitorMeta = (): VisitorMeta => {
    if (typeof window === "undefined") {
        return {
            pageUrl: "",
            referrer: "",
            userAgent: "",
            language: "",
            timezone: "",
        };
    }

    return {
        pageUrl: window.location.href,
        referrer: document.referrer || "",
        userAgent: navigator.userAgent || "",
        language: navigator.language || "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    };
};

const uniqById = (items: ChatMessage[]) => {
    const map = new Map<string, ChatMessage>();
    items.forEach((item) => map.set(item.id, item));
    return Array.from(map.values());
};

const countUserMessages = (items: ChatMessage[]) => items.filter((m) => m.role === "user").length;

const shouldOfferLeadForm = (userMessageCount: number, hasContact: boolean) =>
    !hasContact && userMessageCount >= LEAD_FORM_AFTER_USER_MESSAGES;

export const AgentChatWidget = () => {
    const { t, i18n } = useTranslation("common");
    const { user, isAuthenticated } = useAuth();
    const widgetVisitorKind = useMemo<"guest" | "client">(() => {
        if (!isAuthenticated || !user?.roles?.length) return "guest";
        const codes = user.roles.map((r) => r.code);
        const isStaff = codes.some((c) => c === "ADMIN" || c === "MANAGER" || c === "SUPERADMIN");
        const isClient = codes.includes("CLIENT");
        if (isClient && !isStaff) return "client";
        return "guest";
    }, [isAuthenticated, user]);

    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [input, setInput] = useState("");
    const [isAwaitingReply, setIsAwaitingReply] = useState(false);
    const [showLeadForm, setShowLeadForm] = useState(false);
    const [visitorName, setVisitorName] = useState("");
    const [nameInput, setNameInput] = useState("");
    const [needsNamePrompt, setNeedsNamePrompt] = useState(false);
    const [leadName, setLeadName] = useState("");
    const [leadContact, setLeadContact] = useState("");
    const [leadMessage, setLeadMessage] = useState("");
    const [contactSaved, setContactSaved] = useState(false);
    const [sessionId, setSessionId] = useState("");
    const [backendAvailable, setBackendAvailable] = useState(true);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const replyCursorRef = useRef<string | undefined>(undefined);
    const isOpenRef = useRef(false);
    const lastReadIdRef = useRef<string | null>(null);
    const sessionIdRef = useRef("");

    const markMessagesRead = (items: ChatMessage[], sid = sessionIdRef.current) => {
        const last = [...items].reverse().find((m) => m.role === "agent") ?? items[items.length - 1];
        if (!last || !sid) return;
        lastReadIdRef.current = last.id;
        writeLastReadId(sid, last.id);
        setUnreadCount(0);
    };

    const setChatOpen = (open: boolean) => {
        isOpenRef.current = open;
        writePersistedOpen(open);
        setIsOpen(open);
        if (open) markMessagesRead(messages);
    };

    const canSend = input.trim().length > 0 && !needsNamePrompt;
    const canSendLead = leadContact.trim().length > 0;
    const canStartChat = nameInput.trim().length > 0;

    const resolvedContactName = () => {
        const fromVisitor = visitorName.trim();
        if (fromVisitor) return fromVisitor;
        if (widgetVisitorKind === "client" && user?.fullName?.trim()) return user.fullName.trim();
        return leadName.trim();
    };

    const sendToBackend = async (payload: { messageText?: string; eventType: "message" | "lead" }) => {
        if (!sessionId) return;
        try {
            await chatWidgetMessageApi({
                sessionId,
                eventType: payload.eventType,
                messageText: payload.messageText || "",
                contact: {
                    name: resolvedContactName(),
                    contact: leadContact.trim(),
                    comment: leadMessage.trim(),
                },
                visitor: getVisitorMeta(),
                visitorKind: widgetVisitorKind,
            });
            setBackendAvailable(true);
        } catch {
            setBackendAvailable(false);
        }
    };

    const handleNameSubmit = () => {
        const name = nameInput.trim();
        if (!name || !sessionId) return;

        setVisitorName(name);
        setLeadName(name);
        setNeedsNamePrompt(false);
        setNameInput("");
        if (typeof window !== "undefined") {
            window.localStorage.setItem(visitorNameStorageKey(sessionId), name);
        }
        setMessages((prev) => [
            ...prev.filter((m) => m.id !== "ask-name"),
            {
                id: `hello-${Date.now()}`,
                role: "agent",
                text: t("home.agentChat.niceToMeet", {
                    name,
                    welcome: t("home.agentChat.welcome"),
                }),
            },
        ]);
    };

    const handleSend = async () => {
        const text = input.trim();
        if (!text) return;

        const nextUserMessage = { id: `u-${Date.now()}`, role: "user" as const, text };
        setMessages((prev) => [...prev, nextUserMessage]);
        setInput("");
        setIsAwaitingReply(true);

        const nextUserCount = countUserMessages(messages) + 1;
        setShowLeadForm(shouldOfferLeadForm(nextUserCount, contactSaved));

        void sendToBackend({ eventType: "message", messageText: text });
    };

    const handleLeadSubmit = async () => {
        if (!canSendLead) return;
        if (typeof window !== "undefined" && sessionId) {
            window.localStorage.setItem(
                contactStorageKey(sessionId),
                JSON.stringify({ name: leadName.trim(), contact: leadContact.trim(), comment: leadMessage.trim() }),
            );
        }
        void sendToBackend({ eventType: "lead", messageText: leadMessage.trim() });
        setMessages((prev) => [
            ...prev,
            {
                id: `lead-${Date.now()}`,
                role: "agent",
                text: t("home.agentChat.leadThanks"),
            },
        ]);
        setShowLeadForm(false);
        setContactSaved(true);
        setLeadContact("");
        setLeadMessage("");
    };

    useEffect(() => {
        if (typeof window === "undefined") return;

        const nextSessionId =
            widgetVisitorKind === "client" && user?.id
                ? getAuthenticatedClientSessionId(user.id)
                : getOrCreateGuestSessionId();

        sessionIdRef.current = nextSessionId;
        setSessionId(nextSessionId);

        const restoredOpen = readPersistedOpen();
        isOpenRef.current = restoredOpen;
        setIsOpen(restoredOpen);

        const storedLastRead = readLastReadId(nextSessionId);
        lastReadIdRef.current = storedLastRead;

        const rawContact = window.localStorage.getItem(contactStorageKey(nextSessionId));
        const rawVisitorName = window.localStorage.getItem(visitorNameStorageKey(nextSessionId));
        const rawMessages = window.localStorage.getItem(messagesStorageKey(nextSessionId));

        let savedContact = "";
        if (rawContact) {
            try {
                const parsed = JSON.parse(rawContact) as { name?: string; contact?: string; comment?: string };
                savedContact = parsed.contact?.trim() || "";
                setLeadName(parsed.name || "");
                setLeadContact(parsed.contact || "");
                setLeadMessage(parsed.comment || "");
                if (savedContact) {
                    setContactSaved(true);
                    setShowLeadForm(false);
                }
            } catch {
                /* ignore corrupted storage */
            }
        }

        let loadedMessages: ChatMessage[] = [];
        if (rawMessages) {
            try {
                const parsed = JSON.parse(rawMessages) as ChatMessage[];
                if (Array.isArray(parsed) && parsed.length) {
                    loadedMessages = uniqById(parsed);
                    setMessages(loadedMessages);
                }
            } catch {
                /* ignore corrupted storage */
            }
        }
        if (!loadedMessages.length) {
            loadedMessages = [
                {
                    id: "welcome",
                    role: "agent",
                    text: t("home.agentChat.welcome"),
                },
            ];
            setMessages(loadedMessages);
        }

        // Seed last-read so historic messages don't flash as unread after navigation remount.
        if (!storedLastRead && loadedMessages.length) {
            markMessagesRead(loadedMessages, nextSessionId);
        } else if (restoredOpen) {
            markMessagesRead(loadedMessages, nextSessionId);
        } else {
            setUnreadCount(countUnreadAgentMessages(loadedMessages, storedLastRead));
        }

        const authName =
            widgetVisitorKind === "client" && user?.fullName?.trim() ? user.fullName.trim() : "";
        const storedName = rawVisitorName?.trim() || authName;

        if (storedName) {
            setVisitorName(storedName);
            setLeadName((prev) => prev || storedName);
            setNeedsNamePrompt(false);
        } else if (widgetVisitorKind === "guest") {
            setVisitorName("");
            setNeedsNamePrompt(true);
            setMessages((prev) => {
                if (prev.some((m) => m.id === "ask-name")) return prev;
                return [
                    { id: "ask-name", role: "agent", text: t("home.agentChat.askName") },
                    ...prev.filter((m) => m.id !== "welcome"),
                ];
            });
        } else {
            setNeedsNamePrompt(false);
        }

        if (shouldOfferLeadForm(countUserMessages(loadedMessages), Boolean(savedContact))) {
            setShowLeadForm(true);
        }
    }, [widgetVisitorKind, user?.id, t]);

    useEffect(() => {
        setMessages((prev) => {
            if (!prev.length) {
                return [{ id: "welcome", role: "agent", text: t("home.agentChat.welcome") }];
            }
            return prev.map((m) => {
                if (m.id === "welcome") return { ...m, text: t("home.agentChat.welcome") };
                if (m.id === "ask-name") return { ...m, text: t("home.agentChat.askName") };
                return m;
            });
        });
    }, [i18n.language, t]);

    useEffect(() => {
        if (typeof window === "undefined" || !sessionId) return;
        window.localStorage.setItem(messagesStorageKey(sessionId), JSON.stringify(messages));
    }, [messages, sessionId]);

    useEffect(() => {
        if (!sessionId) return;
        if (isOpen) {
            markMessagesRead(messages);
            return;
        }
        setUnreadCount(countUnreadAgentMessages(messages, lastReadIdRef.current));
    }, [messages, isOpen, sessionId]);

    const hasUserChat = countUserMessages(messages) > 0;
    const shouldSyncReplies = Boolean(sessionId) && (isOpen || hasUserChat);

    useEffect(() => {
        if (!shouldSyncReplies || !sessionId) return;

        replyCursorRef.current = undefined;

        const applyReplies = (
            incoming: Array<{ id: string; text: string; createdAt?: string }>,
            nextCursor?: string | null,
        ) => {
            if (!incoming.length) {
                if (nextCursor) replyCursorRef.current = nextCursor;
                return;
            }
            setBackendAvailable(true);
            setMessages((prev) =>
                uniqById([
                    ...prev,
                    ...incoming.map((m) => ({ id: m.id, role: "agent" as const, text: m.text })),
                ]),
            );
            setIsAwaitingReply(false);
            const last = incoming[incoming.length - 1];
            replyCursorRef.current = nextCursor || last.createdAt || last.id;
        };

        const loadReplies = async () => {
            try {
                const data = await chatWidgetRepliesApi(sessionId, replyCursorRef.current);
                applyReplies(data.messages ?? [], data.nextCursor);
            } catch {
                setBackendAvailable(false);
            }
        };

        void loadReplies();
    }, [shouldSyncReplies, sessionId]);

    useChatWidgetRepliesSocket(shouldSyncReplies ? sessionId : "", (message) => {
        setBackendAvailable(true);
        setMessages((prev) =>
            uniqById([...prev, { id: message.id, role: "agent", text: message.text }]),
        );
        setIsAwaitingReply(false);
        // Prefer createdAt so HTTP poll uses a time cursor (UUID id > since skips replies).
        replyCursorRef.current = message.createdAt || message.id;
    });

    useEffect(() => {
        if (!shouldSyncReplies || !sessionId || typeof window === "undefined") return;

        let pollTimer: number | undefined;

        const pollOnce = () => {
            void chatWidgetRepliesApi(sessionId, replyCursorRef.current)
                .then((data) => {
                    const incoming = data.messages ?? [];
                    if (incoming.length) {
                        setBackendAvailable(true);
                        setMessages((prev) =>
                            uniqById([
                                ...prev,
                                ...incoming.map((m) => ({ id: m.id, role: "agent" as const, text: m.text })),
                            ]),
                        );
                        setIsAwaitingReply(false);
                    }
                    if (data.nextCursor) {
                        replyCursorRef.current = data.nextCursor;
                    } else if (incoming.length) {
                        const last = incoming[incoming.length - 1];
                        replyCursorRef.current = last.createdAt || last.id;
                    }
                })
                .catch(() => setBackendAvailable(false));
        };

        // Poll only while chat is active — avoids /replies 404 spam for idle guests.
        pollTimer = window.setInterval(pollOnce, 8000);
        pollOnce();

        return () => {
            if (pollTimer) window.clearInterval(pollTimer);
        };
    }, [shouldSyncReplies, sessionId]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const onOpenRequest = () => {
            isOpenRef.current = true;
            writePersistedOpen(true);
            setIsOpen(true);
        };
        window.addEventListener(OPEN_AGENT_CHAT_WIDGET_EVENT, onOpenRequest);
        return () => window.removeEventListener(OPEN_AGENT_CHAT_WIDGET_EVENT, onOpenRequest);
    }, []);

    return (
        <div className={cx("widget")}>
            {isOpen ? (
                <section className={cx("panel")} aria-label={t("home.agentChat.panelAria")}>
                    <header className={cx("header")}>
                        <div className={cx("title-wrap")}>
                            <span className={cx("status-dot")} aria-hidden />
                            <div>
                                <strong>Tezam Agent</strong>
                                <p>{t("home.agentChat.statusOnline")}</p>
                            </div>
                        </div>
                        {visitorName ? (
                            <small className={cx("session")}>{visitorName}</small>
                        ) : null}
                        <button
                            type="button"
                            className={cx("icon-btn")}
                            onClick={() => setChatOpen(false)}
                            aria-label={t("home.agentChat.collapse")}
                        >
                            ×
                        </button>
                    </header>

                    <div className={cx("messages")}>
                        {needsNamePrompt ? (
                            <div className={cx("name-form")}>
                                <strong>{t("home.agentChat.namePromptTitle")}</strong>
                                <p>{t("home.agentChat.namePromptHint")}</p>
                                <input
                                    value={nameInput}
                                    onChange={(e) => setNameInput(e.target.value)}
                                    placeholder={t("home.agentChat.namePlaceholder")}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleNameSubmit();
                                    }}
                                />
                                <button type="button" onClick={handleNameSubmit} disabled={!canStartChat}>
                                    {t("home.agentChat.startChat")}
                                </button>
                            </div>
                        ) : null}
                        {!needsNamePrompt && isAwaitingReply ? (
                            <div className={cx("waiting")}>{t("home.agentChat.waiting")}</div>
                        ) : null}
                        {messages.map((m) => (
                            <div key={m.id} className={cx("msg", m.role === "user" ? "user" : "agent")}>
                                {m.text}
                            </div>
                        ))}
                        {showLeadForm ? (
                            <div className={cx("lead-form")}>
                                <strong>{t("home.agentChat.leadTitle")}</strong>
                                <p>{t("home.agentChat.leadText")}</p>
                                <input
                                    value={leadName}
                                    onChange={(e) => setLeadName(e.target.value)}
                                    placeholder={t("home.agentChat.namePlaceholder")}
                                />
                                <input
                                    value={leadContact}
                                    onChange={(e) => setLeadContact(e.target.value)}
                                    placeholder={t("home.agentChat.leadContactPlaceholder")}
                                />
                                <textarea
                                    value={leadMessage}
                                    onChange={(e) => setLeadMessage(e.target.value)}
                                    placeholder={t("home.agentChat.leadQuestionPlaceholder")}
                                    rows={2}
                                />
                                <button type="button" onClick={handleLeadSubmit} disabled={!canSendLead}>
                                    {t("home.agentChat.leadSubmit")}
                                </button>
                            </div>
                        ) : null}
                    </div>

                    <footer className={cx("footer")}>
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={
                                needsNamePrompt
                                    ? t("home.agentChat.inputNeedName")
                                    : t("home.agentChat.inputPlaceholder")
                            }
                            disabled={needsNamePrompt}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleSend();
                            }}
                        />
                        <button type="button" onClick={handleSend} disabled={!canSend}>
                            {t("home.agentChat.send")}
                        </button>
                    </footer>
                </section>
            ) : null}

            <button
                type="button"
                className={cx("fab", { hasUnread: unreadCount > 0 })}
                onClick={() => setChatOpen(!isOpen)}
                aria-label={
                    unreadCount > 0
                        ? t("home.agentChat.titleUnread", { count: unreadCount })
                        : t("home.agentChat.title")
                }
                title={t("home.agentChat.title")}
            >
                {unreadCount > 0 ? (
                    <span className={cx("fab-badge")} aria-hidden>
                        {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                ) : null}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                        d="M4.5 6.75A2.25 2.25 0 0 1 6.75 4.5h10.5A2.25 2.25 0 0 1 19.5 6.75v7.5a2.25 2.25 0 0 1-2.25 2.25H9.31L6 19.5v-2.25H6.75A2.25 2.25 0 0 1 4.5 15V6.75Z"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>
        </div>
    );
};

